import { FC, useState } from 'react';
import { Field, Form } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import { ReactComponent as WithdrawIcon } from 'assets/icons/button-icons/upload.svg';
import { DecimalPlaces } from 'constants/decimals/decimal-places';
import { FormApi } from 'final-form';
import { VerificationInfo } from 'pages/PageVerification/components/logic/VerificationInfo';
import { EColors } from 'styles/style-variables/colors';

import { CurrencyBadge } from 'components/ui/CurrencyBadge';
import { getVerificationPanelText } from 'utils/common/get-verification-panel-text.util';
import { formatInputValue } from 'utils/formatters/format-input-value.util';
import { formatUsdOutput } from 'utils/formatters/format-usd-output.util';
import { composeValidators } from 'utils/validators/compose-validators';
import { isGreaterOrEqual } from 'utils/validators/is-greater-or-equal';
import { isLessOrEqual } from 'utils/validators/is-less-or-equal';
import { isNumber } from 'utils/validators/is-number';
import { isRequired } from 'utils/validators/is-required';

import { MAXIMUM_WITHDRAWAL_AMOUNT_DAILY_FOR_BASIC_AUTH } from './constants/maximum-withdrawal-amount';
import { MINIMUM_BTC_WITHDRAW_AMOUNT } from './constants/minimum-withdraw-amount.const';
import { NETWORK_COMMISSION_AMOUNT_BTC } from './constants/network-commission-amount-btc.const';
import { IWithdrawBtcFormValues } from './types/withdraw-btc-form-values.interface';

import * as S from './WithdrawForm.styled';

interface IWithdrawProps {
  btcAmountInWallet: number;
  btcUsdRate: number;
  savedWallet: string;
  handleFormSubmit: (values: IWithdrawBtcFormValues) => void;
  isBasicVerification: boolean;
}

export const WithdrawForm: FC<IWithdrawProps> = ({
  btcAmountInWallet,
  btcUsdRate,
  savedWallet,
  handleFormSubmit,
  isBasicVerification,
}) => {
  const { t } = useTranslation();
  const [btcAmount, setBtcAmount] = useState('');
  const [usdAmount, setUsdAmount] = useState('≈ $0');
  const verificationInfo = getVerificationPanelText(true);

  const handleBtcAmountChange = (
    value: string,
    form: FormApi<IWithdrawBtcFormValues, Partial<IWithdrawBtcFormValues>>,
  ) => {
    const formattedAmount = formatInputValue(value, DecimalPlaces.Btc);
    setBtcAmount(formattedAmount);
    setUsdAmount(`≈ $${formatUsdOutput(Number(formattedAmount) * btcUsdRate)}`);
    form.change('amount', formattedAmount);
  };

  const formatErrorMessage = (error: string) => {
    if (error.includes('Value should be less or equal to')) {
      const value = error.split(' ').find((item) => +item > 0);

      return `${t('withdrawContent.valueShouldBeLessOrEqualTo')}${value && ' ' + value}`;
    }

    if (error.includes('Value should be greater or equal to')) {
      return t('withdrawContent.valueShouldBeGreaterOrEqualTo00005');
    }

    if (error.includes('This field is required')) {
      return t('withdrawContent.requiredField');
    }

    return error;
  };

  return (
    <Form onSubmit={handleFormSubmit} initialValues={{ amount: btcAmount }}>
      {({ handleSubmit, submitFailed, hasValidationErrors, form }) => (
        <S.Form onSubmit={handleSubmit}>
          <S.FieldsWrapper $isFailed={submitFailed}>
            <Field
              name="amount"
              validate={composeValidators([
                isRequired,
                isNumber,
                isGreaterOrEqual(MINIMUM_BTC_WITHDRAW_AMOUNT),
                isLessOrEqual(btcAmountInWallet),
              ])}
            >
              {({ input, meta }) => (
                <S.AmountLabel>
                  <S.LabelText>{t('withdrawContent.amount')}</S.LabelText>
                  <S.WithdrawAmountInput
                    {...input}
                    type="text"
                    cryptoCurrency="btc"
                    rightText={usdAmount}
                    rightTextColor={
                      isBasicVerification &&
                      +form.getState().values.amount * btcUsdRate >
                        MAXIMUM_WITHDRAWAL_AMOUNT_DAILY_FOR_BASIC_AUTH
                        ? EColors.Red1
                        : undefined
                    }
                    hasErrors={meta.error && submitFailed}
                    onChange={(evt) => handleBtcAmountChange(evt.currentTarget.value, form)}
                    autoComplete="off"
                    autoFocus={true}
                  />
                  <S.InputDescriptionText>
                    {t('withdrawContent.minimumWithdrawalAmount')} {MINIMUM_BTC_WITHDRAW_AMOUNT} BTC
                  </S.InputDescriptionText>
                  {meta.error && submitFailed && (
                    <S.ErrorText>{formatErrorMessage(meta.error as string)}</S.ErrorText>
                  )}
                </S.AmountLabel>
              )}
            </Field>
            {isBasicVerification &&
              +form.getState().values.amount * btcUsdRate >
                MAXIMUM_WITHDRAWAL_AMOUNT_DAILY_FOR_BASIC_AUTH && (
                <VerificationInfo
                  data={[verificationInfo[1]]}
                  isLimitWithdrawalPanel
                  isWithdrawalView
                />
              )}
            <S.NetworkCommissionInfo>
              <S.NetworkCommissionTitle>
                {t('withdrawContent.networkCommission')}
              </S.NetworkCommissionTitle>
              <S.NetworkCommissionWrapper>
                <CurrencyBadge currency="btc" />
                <S.CommissionAmount>{NETWORK_COMMISSION_AMOUNT_BTC}</S.CommissionAmount>
              </S.NetworkCommissionWrapper>
            </S.NetworkCommissionInfo>

            <S.WalletAddressWrapper>
              <S.WalletTitle>{t('withdrawContent.toWallet')}</S.WalletTitle>
              <S.SavedWalletContainer>
                <S.WalletAddress>{savedWallet}</S.WalletAddress>
                <S.WalletPrompt>{t('withdrawContent.savedWallet')}</S.WalletPrompt>
              </S.SavedWalletContainer>
            </S.WalletAddressWrapper>
          </S.FieldsWrapper>

          <S.WithdrawButton
            type="submit"
            text={t('withdrawContent.withdraw')}
            disabled={
              (submitFailed && hasValidationErrors) ||
              (isBasicVerification &&
                +form.getState().values.amount * btcUsdRate >
                  MAXIMUM_WITHDRAWAL_AMOUNT_DAILY_FOR_BASIC_AUTH)
            }
            icon={<WithdrawIcon />}
          />
        </S.Form>
      )}
    </Form>
  );
};
